// Shared closing-time display windows (BL-1116).
//
// These are DISPLAY policy, not lifecycle policy — nothing here decides when a
// lot actually closes (that is the server's job, per ADR 024 § 1). They decide
// which FORM a surface renders: a calendar date, or a live countdown.
//
// They live in `@auctogon/time` because admin and bidder must agree. When they
// disagreed — a copy in a bidder component file and literals in three admin
// files — a buyer could see a lot treated as "closing today" on one screen and
// not on another. See BL-1329 for the wider consolidation this is the first
// slice of.

/**
 * How far ahead a close time may be and still render as a live countdown.
 *
 * Beyond it, surfaces render the day and time instead: nobody converts
 * "36h 44m" into "Thursday afternoon" in their head.
 *
 * Deliberately a ROLLING 24 hours, not "closes today" — a calendar-day rule
 * breaks at the boundary, where a lot closing 12:30 AM tomorrow is 40 minutes
 * away and must count down rather than render as a date.
 */
export const COUNTDOWN_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * How close to its close a lot must be before every surface treats it as
 * "closing soon" — the urgency band, distinct from the wider
 * {@link COUNTDOWN_WINDOW_MS} gate above that decides whether a countdown is
 * shown at all.
 *
 * **One hour, everywhere.** Buyer surfaces and the clerk console read this same
 * value, and it is the same hour the `lot_closing_soon` / `auction_ending_1hr`
 * notifications already fire on (BL-319) — so the email a buyer receives and
 * the screen they open agree. Before BL-1329 they did not: the notification
 * said one hour, the bidder card and detail panel said fifteen minutes (equal
 * only by coincidence — one imported a constant, the other hardcoded `900`),
 * and the clerk console said two.
 *
 * **This retires the clerk console's two-minute band** (BL-1255 AC 6), a
 * deliberate product decision taken 2026-09-14 (Michael Lawrie) in favour of one
 * meaning across the product. Its consequence is stated rather than discovered:
 * on a sale at the default 60-second stagger (`tenants.default_stagger_seconds`)
 * roughly 60 catalog rows now carry the urgency treatment at once, where two did
 * before. Recorded in ADR 010 — Isomorphic Services, constraint 5, so a later
 * reader does not "fix" it back.
 *
 * **This file is the only place a closing threshold may be written.** Every
 * other module imports; `closingband/no-closing-threshold-literal` fails the
 * build on a threshold literal anywhere else. See
 * `.claude/rules/closing-band-ownership.md`.
 */
export const CLOSING_SOON_MS = 60 * 60 * 1000;
